/**
 * The camera suite: five views, one camera, and a blend between them.
 *
 * ## One camera, not five
 *
 * The renderer, the shadow cascades, the cloud pass and the water all take *the* camera, and the
 * post chain captures it at construction. Swapping the object out on a view change would mean
 * telling five subsystems about it, and one of them would be forgotten. So there is a single
 * `PerspectiveCamera` and the modes are *pose generators*: each computes where it would like the
 * camera to be, and the rig writes the winner (or a blend of two) into the one camera.
 *
 * That also makes transitions almost free. A cut between two views is jarring in a way that is
 * hard to articulate and impossible to miss; a blend is one `lerp` and one `slerp` between two
 * poses that were going to be computed anyway.
 *
 * ## The five, and what each is for
 *
 *   - **Cockpit** — the pilot's eye, rigidly attached to the airframe, with two deliberate
 *     exceptions that are the whole reason this is not just "camera at the eye point": a g-induced
 *     head shift, and a touchdown shake scaled by sink rate. Both are described below.
 *   - **Chase** — spring-damped behind the aircraft, orbitable with the mouse. The M2 rig.
 *   - **Fly-by** — a tripod on the ground that the aircraft flies past, and which *leapfrogs*
 *     ahead once the aircraft is behind it. The one view that shows an aeroplane as an object in
 *     a landscape rather than as a thing you are attached to.
 *   - **Orbit** — a fixed radius circling the aircraft on its own, for looking at the aeroplane.
 *   - **Free** — a drone: it holds position in the world and the mouse flies it, so the aircraft
 *     can be left behind entirely.
 *
 * ## Why the cockpit camera moves at all
 *
 * A cockpit view welded to the airframe is *correct* and reads as dead. Two effects fix it, and
 * both are physical rather than decorative:
 *
 *   - **G-shift.** Under load a pilot's head and torso compress downward, and under negative g
 *     they rise. A few centimetres, low-passed with a time constant of about a fifth of a second.
 *     It is small enough that nobody notices it directly and large enough that a 2 g turn feels
 *     like one.
 *   - **Touchdown shake.** A decaying oscillation triggered on ground contact and scaled by the
 *     *sink rate at the moment of contact*, which is the number that determines how hard the
 *     arrival was. A greaser produces almost nothing; an arrival you would apologise for rattles
 *     the panel. Scaling by anything else — speed, weight on wheels — gets that backwards.
 *
 * Neither touches the aircraft's physics. They move the eye, which is exactly what happens in
 * the aeroplane.
 *
 * `updateFrame(alpha, dt)`: yes — {@link CameraRig.updateFrame}, once per rendered frame, from
 * the interpolated flight state. Allocation-free after construction.
 */

import { PerspectiveCamera, Quaternion, Vector3 } from 'three';
import { DEG2RAD, clamp, expDamp, wrapTwoPi } from '../core/math.js';
import type { CameraMode } from '../game/settings.js';

/** The order `C` cycles through. Cockpit first: it is where a session should start looking. */
export const CAMERA_CYCLE: readonly CameraMode[] = ['cockpit', 'chase', 'flyby', 'orbit', 'free'];

export interface CameraRigOptions {
  fovDeg?: number;
  cockpitFovDeg?: number;
  nearM?: number;
  farM?: number;
  /** Chase distance [m]. */
  chaseDistanceM?: number;
  /** Seconds to blend between two views. */
  transitionS?: number;
}

/** What the rig needs to know about the aircraft this frame. */
export interface CameraTarget {
  /** Render-space position of the aircraft's centre of gravity. */
  position: Vector3;
  /** Render-space attitude. */
  attitude: Quaternion;
  /** Render-space velocity [m/s]. Drives the fly-by's leapfrog and the drone's inertia. */
  velocity: Vector3;
  /** Load factor [g]. Drives the cockpit head shift. */
  loadFactor: number;
  /** True while any wheel is on the ground. */
  onGround: boolean;
  /** Height above the ground [m]. Keeps the fly-by tripod at a sane altitude. */
  heightAboveGroundM: number;
}

export function makeCameraTarget(): CameraTarget {
  return {
    position: new Vector3(),
    attitude: new Quaternion(),
    velocity: new Vector3(),
    loadFactor: 1,
    onGround: true,
    heightAboveGroundM: 0,
  };
}

/** A camera pose. Two of these are blended to make a transition. */
interface Pose {
  position: Vector3;
  orientation: Quaternion;
  fovDeg: number;
}

function makePose(): Pose {
  return { position: new Vector3(), orientation: new Quaternion(), fovDeg: 55 };
}

// Module-level scratch. Every one of these is written before it is read within a single call.
const scratchVector = /* #__PURE__ */ new Vector3();
const scratchVectorB = /* #__PURE__ */ new Vector3();
const scratchQuaternion = /* #__PURE__ */ new Quaternion();
const scratchUp = /* #__PURE__ */ new Vector3(0, 1, 0);
const scratchMatrixTarget = /* #__PURE__ */ new Vector3();

/** How long the touchdown shake rings for [s]. */
const SHAKE_DURATION_S = 0.9;
/** Shake frequency [Hz] — fast enough to read as an impact, slow enough to be visible. */
const SHAKE_HZ = 11;
/** Sink rate at which the shake is at full amplitude [m/s]. Above this it is clamped. */
const SHAKE_REFERENCE_SINK_MPS = 3.5;
/** Full-amplitude shake displacement [m]. */
const SHAKE_AMPLITUDE_M = 0.055;

/** Metres of head travel per g away from 1. Downward under positive g. */
const G_SHIFT_M_PER_G = 0.022;
/** Time constant on the head shift [s] — a body, not a spring. */
const G_SHIFT_HALF_LIFE_S = 0.18;

export class CameraRig {
  readonly camera: PerspectiveCamera;

  /** Eye point in body axes [m] — set from the aircraft config. */
  private readonly eyePointBody = new Vector3(0.5, -0.35, -0.75);

  private modeValue: CameraMode = 'chase';
  /** Cockpit fraction at the moment a blend was interrupted; see {@link cockpitFraction}. */
  private snapshotCockpitFraction = 0;
  /** The mode being blended *out of*; null when the transition is finished. */
  private previousMode: CameraMode | null = null;
  /**
   * True when the outgoing pose is a frozen snapshot rather than a live mode.
   *
   * Set when a mode change *interrupts* a transition. Re-deriving the outgoing mode's own pose
   * in that case is wrong and visibly so: the camera is currently somewhere between two views,
   * and blending from where the interrupted view *would* have been is a jump to it first —
   * measured, 25 m in one frame. Blending from where the camera actually is cannot jump by
   * construction.
   */
  private fromSnapshot = false;
  private transition = 1;
  private readonly transitionS: number;

  private fovDeg: number;
  private cockpitFovDeg: number;

  // --- chase / orbit ---------------------------------------------------------------------
  private chaseYaw = 205 * DEG2RAD;
  private chasePitch = 14 * DEG2RAD;
  private chaseDistance: number;
  private readonly chasePosition = new Vector3();
  /**
   * The smoothed camera-to-aircraft offset — the chase view's actual state since v1.1.
   *
   * Filtering this rather than the world position is what makes the standoff independent of
   * airspeed and of frame time; see {@link chasePose}. Origin-invariant by construction, so a
   * floating-origin rebase cannot disturb it.
   */
  private readonly chaseOffset = new Vector3();
  private chaseInitialised = false;
  private orbitAngle = 0;

  // --- fly-by ------------------------------------------------------------------------------
  /** Where the tripod is standing. Moved by {@link leapfrog}, never per frame. */
  private readonly flybyPosition = new Vector3();
  private flybyPlaced = false;

  // --- free / drone ---------------------------------------------------------------------------
  private readonly dronePosition = new Vector3();
  private droneYaw = 0;
  private dronePitch = 0;
  private droneSpeedMps = 0;
  private dronePlaced = false;

  // --- cockpit feel ----------------------------------------------------------------------------
  private gShiftM = 0;
  private shakeTimeS = Number.POSITIVE_INFINITY;
  private shakeAmplitudeM = 0;
  private wasOnGround = true;

  private readonly current = makePose();
  private readonly other = makePose();
  private readonly focus = new Vector3();

  constructor(options: CameraRigOptions = {}) {
    this.fovDeg = options.fovDeg ?? 55;
    this.cockpitFovDeg = options.cockpitFovDeg ?? 45;
    this.chaseDistance = options.chaseDistanceM ?? 22;
    this.transitionS = options.transitionS ?? 0.55;

    this.camera = new PerspectiveCamera(
      this.fovDeg,
      1,
      options.nearM ?? 0.12,
      options.farM ?? 60_000,
    );
    this.camera.name = 'camera';
  }

  get mode(): CameraMode {
    return this.modeValue;
  }

  /** True while a view change is still blending. */
  get isTransitioning(): boolean {
    return this.transition < 1;
  }

  /**
   * How much of the mix is "inside the cockpit", 0…1 — the audio system's perspective blend.
   *
   * A fraction rather than a boolean because the camera *blends* between views over half a
   * second, and a mix that switched from muffled to open in one frame while the picture was
   * still moving would be the most obvious artefact in the whole audio system. This follows the
   * same smoothstepped `t` the pose does, so the sound arrives exactly with the view.
   */
  get cockpitFraction(): number {
    if (this.transition >= 1) return this.modeValue === 'cockpit' ? 1 : 0;
    const t = this.transition * this.transition * (3 - 2 * this.transition);
    const to = this.modeValue === 'cockpit' ? 1 : 0;
    // An interrupted blend has no outgoing *mode* to ask, so the fraction it was at when it was
    // interrupted is captured in `setMode` and used as the origin instead.
    const from = this.fromSnapshot
      ? this.snapshotCockpitFraction
      : this.previousMode === null
        ? to
        : this.previousMode === 'cockpit'
          ? 1
          : 0;
    return from + (to - from) * t;
  }

  /**
   * Switch view, blending from wherever the camera currently is.
   *
   * Interrupting a transition restarts from the *blended* pose rather than from the mode being
   * left, which is what stops a fast double-press from snapping.
   */
  setMode(mode: CameraMode): void {
    if (mode === this.modeValue) return;
    if (this.transition < 1) {
      // Interrupting a blend: freeze where the camera actually is and go from there. See
      // {@link fromSnapshot}.
      this.other.position.copy(this.camera.position);
      this.other.orientation.copy(this.camera.quaternion);
      this.other.fovDeg = this.camera.fov;
      this.snapshotCockpitFraction = this.cockpitFraction;
      this.fromSnapshot = true;
      this.previousMode = null;
    } else {
      this.fromSnapshot = false;
      this.previousMode = this.modeValue;
    }
    this.modeValue = mode;
    this.transition = 0;
    // A view that holds a world position should re-establish it where the camera is now, rather
    // than resuming from wherever it was last time it was selected.
    if (mode === 'free') this.dronePlaced = false;
    if (mode === 'flyby') this.flybyPlaced = false;
  }

  cycle(): void {
    const index = CAMERA_CYCLE.indexOf(this.modeValue);
    this.setMode(CAMERA_CYCLE[(index + 1) % CAMERA_CYCLE.length]);
  }

  /**
   * Carry the rig's world-anchored state through a floating-origin rebase.
   *
   * Three of the five views keep a position in *render space* between frames rather than deriving
   * one from the aircraft each time, and render space is exactly what a rebase moves. Without this
   * they are all silently dragged along with the origin:
   *
   *   - **chase** smooths toward its ideal offset with a 0.07 s half-life, so a rebase injects the
   *     whole shift as a step into that filter and the camera is whipped backwards and reels itself
   *     in over about half a second. This is the one that matters most, because chase is the
   *     default view and at a 1.9 s rebase period it would spend roughly a quarter of every flight
   *     visibly recovering.
   *   - **fly-by** is a tripod standing on the ground. Dragging it forward with the origin means
   *     the aircraft never actually overtakes it, so the leapfrog that makes the view work stops
   *     firing and the aeroplane becomes a permanent dot ahead of a camera sliding across the
   *     landscape.
   *   - **free/drone** exists precisely to hold still while the aircraft leaves. Uncarried, the
   *     furthest it could ever get from the aeroplane is one rebase threshold.
   *
   * The blend snapshot is carried too, but only when it is a real frozen pose: an interrupted
   * transition stores where the camera actually was (see {@link setMode}), and that is world state
   * like any other. When the blend is between two live modes, `other` is recomputed from the
   * aircraft every frame and carrying it would double-apply the shift.
   *
   * Deltas are render-space — x east, y up, z south — matching `effects.applyOriginShift`. Not
   * carried: `focus`, recomputed from the target every frame; `camera.position`, rewritten from the
   * pose in `updateFrame` before anything draws.
   *
   * `updateFrame`-adjacent: called from the fixed step, on the frames the origin moved. Allocates
   * nothing.
   */
  applyOriginShift(deltaX: number, deltaY: number, deltaZ: number): void {
    if (this.chaseInitialised) {
      this.chasePosition.x += deltaX;
      this.chasePosition.y += deltaY;
      this.chasePosition.z += deltaZ;
    }
    if (this.flybyPlaced) {
      this.flybyPosition.x += deltaX;
      this.flybyPosition.y += deltaY;
      this.flybyPosition.z += deltaZ;
    }
    if (this.dronePlaced) {
      this.dronePosition.x += deltaX;
      this.dronePosition.y += deltaY;
      this.dronePosition.z += deltaZ;
    }
    if (this.fromSnapshot && this.transition < 1) {
      this.other.position.x += deltaX;
      this.other.position.y += deltaY;
      this.other.position.z += deltaZ;
    }
  }

  setEyePoint(bodyPoint: readonly [number, number, number]): void {
    // Body axes are FRD (x forward, y right, z down); render axes are x right, y up, z back.
    this.eyePointBody.set(bodyPoint[1], -bodyPoint[2], -bodyPoint[0]);
  }

  setFieldOfView(externalDeg: number, cockpitDeg: number): void {
    this.fovDeg = clamp(externalDeg, 25, 110);
    this.cockpitFovDeg = clamp(cockpitDeg, 25, 110);
  }

  setViewport(widthPx: number, heightPx: number): void {
    this.camera.aspect = widthPx / Math.max(1, heightPx);
    this.camera.updateProjectionMatrix();
  }

  setFarPlane(farM: number): void {
    this.camera.far = farM;
    this.camera.updateProjectionMatrix();
  }

  /** Rotate the chase orbit, or steer the drone, depending on which is active. */
  orbit(deltaYawRad: number, deltaPitchRad: number): void {
    if (this.modeValue === 'free') {
      this.droneYaw = wrapTwoPi(this.droneYaw + deltaYawRad);
      this.dronePitch = clamp(this.dronePitch + deltaPitchRad, -80 * DEG2RAD, 80 * DEG2RAD);
      return;
    }
    this.chaseYaw = wrapTwoPi(this.chaseYaw + deltaYawRad);
    // Stop short of the poles: straight down the Y axis makes the up vector ambiguous and the
    // view rolls.
    this.chasePitch = clamp(this.chasePitch + deltaPitchRad, -75 * DEG2RAD, 85 * DEG2RAD);
  }

  /**
   * Put the chase camera at a given distance [m].
   *
   * The same clamp the wheel obeys, because the reason for the clamp is the near plane and the
   * shadow cascades, not the input device. Exists for the scripted aircraft portraits: 22 m frames
   * a 15 m fighter and an 8 m aerobat very differently, and a picture meant to show the aeroplane
   * has to be framed on the aeroplane.
   */
  setChaseDistance(metres: number): void {
    this.chaseDistance = clamp(metres, 5, 400);
    this.chaseInitialised = false;
  }

  /** Dolly the chase view, or set the drone's speed. `factor` is multiplicative. */
  dolly(factor: number): void {
    if (this.modeValue === 'free') {
      this.droneSpeedMps = clamp(this.droneSpeedMps * factor, 0, 400);
      if (this.droneSpeedMps < 0.5 && factor > 1) this.droneSpeedMps = 2;
      return;
    }
    this.chaseDistance = clamp(this.chaseDistance * factor, 5, 400);
  }

  /** Fly the drone. Components are −1…1 in its own axes: right, up, forward. */
  private readonly droneCommand = new Vector3();
  setDroneCommand(right: number, up: number, forward: number): void {
    this.droneCommand.set(clamp(right, -1, 1), clamp(up, -1, 1), clamp(forward, -1, 1));
  }

  /** The point the view is centred on — what the shadow cascades are fitted around. */
  get focusPoint(): Vector3 {
    return this.focus;
  }

  /**
   * Trigger the touchdown shake.
   *
   * Called by the frame update when the wheels arrive; exposed so a scripted run or a test can
   * drive it directly. `sinkRateMps` is the rate at the moment of contact — the number that says
   * how hard the arrival was.
   */
  bumpTouchdown(sinkRateMps: number): void {
    const strength = clamp(Math.abs(sinkRateMps) / SHAKE_REFERENCE_SINK_MPS, 0, 1);
    // A greaser produces almost nothing. Squared, so the difference between a good landing and a
    // firm one is obvious and the difference between firm and terrible is dramatic.
    const amplitude = strength * strength * SHAKE_AMPLITUDE_M;
    // Never *reduce* a shake that is already ringing: a bounce is two arrivals, and the second
    // one is usually the one you felt.
    if (amplitude <= this.currentShakeAmplitude()) return;
    this.shakeAmplitudeM = amplitude;
    this.shakeTimeS = 0;
  }

  /** Amplitude the current shake has decayed to [m]. */
  private currentShakeAmplitude(): number {
    if (!Number.isFinite(this.shakeTimeS) || this.shakeTimeS >= SHAKE_DURATION_S) return 0;
    const remaining = 1 - this.shakeTimeS / SHAKE_DURATION_S;
    return this.shakeAmplitudeM * remaining * remaining;
  }

  /**
   * Current shake displacement [m], for tests and diagnostics.
   *
   * **Cosine, not sine.** An impact is an instantaneous displacement that then rings out; a sine
   * starts at exactly zero, so the first frame of a heavy arrival showed nothing at all and the
   * shake *built up* over the following few — which reads as the aeroplane wobbling after
   * landing rather than as hitting the ground.
   */
  get shakeDisplacementM(): number {
    const amplitude = this.currentShakeAmplitude();
    if (amplitude === 0) return 0;
    return amplitude * Math.cos(this.shakeTimeS * SHAKE_HZ * Math.PI * 2);
  }

  /** Head shift from load factor [m], positive down. For tests and diagnostics. */
  get gShiftDisplacementM(): number {
    return this.gShiftM;
  }

  /**
   * Place the camera for this frame.
   *
   * @param target      interpolated aircraft state.
   * @param dtSeconds   real frame time.
   * @param sinkRateMps sink rate at the last touchdown, for the shake.
   */
  updateFrame(target: CameraTarget, dtSeconds: number, sinkRateMps = 0): void {
    const dt = Math.max(0, dtSeconds);

    // --- cockpit feel, advanced whatever view is active so a mode change does not reset it ---
    // Positive g pushes the head down; the body follows over a fifth of a second or so.
    const gTarget = (target.loadFactor - 1) * G_SHIFT_M_PER_G;
    this.gShiftM = expDamp(this.gShiftM, gTarget, G_SHIFT_HALF_LIFE_S, dt);
    if (Number.isFinite(this.shakeTimeS)) this.shakeTimeS += dt;
    if (target.onGround && !this.wasOnGround) this.bumpTouchdown(sinkRateMps);
    this.wasOnGround = target.onGround;

    // --- the active pose, and the one being left ------------------------------------------
    this.poseFor(this.modeValue, target, dt, this.current);

    if (this.transition < 1) {
      // The outgoing view is still advanced — a chase camera that froze while blending out would
      // visibly lag the aircraft for the length of the transition. Unless it is a snapshot from
      // an interrupted transition, which by definition has no mode to advance.
      if (!this.fromSnapshot && this.previousMode !== null) {
        this.poseFor(this.previousMode, target, dt, this.other);
      }
      this.transition = Math.min(1, this.transition + dt / Math.max(1e-3, this.transitionS));
      // Smoothstep, so the blend has no corner at either end.
      const t = this.transition * this.transition * (3 - 2 * this.transition);
      this.camera.position.lerpVectors(this.other.position, this.current.position, t);
      this.camera.quaternion.slerpQuaternions(this.other.orientation, this.current.orientation, t);
      const fov = this.other.fovDeg + (this.current.fovDeg - this.other.fovDeg) * t;
      if (Math.abs(this.camera.fov - fov) > 1e-4) {
        this.camera.fov = fov;
        this.camera.updateProjectionMatrix();
      }
      if (this.transition >= 1) {
        this.previousMode = null;
        this.fromSnapshot = false;
      }
    } else {
      this.camera.position.copy(this.current.position);
      this.camera.quaternion.copy(this.current.orientation);
      if (Math.abs(this.camera.fov - this.current.fovDeg) > 1e-4) {
        this.camera.fov = this.current.fovDeg;
        this.camera.updateProjectionMatrix();
      }
    }

    this.camera.updateMatrixWorld();
  }

  /** Compute one mode's pose into `out`. */
  private poseFor(mode: CameraMode, target: CameraTarget, dt: number, out: Pose): void {
    switch (mode) {
      case 'cockpit':
        this.cockpitPose(target, out);
        return;
      case 'chase':
        this.chasePose(target, dt, out);
        return;
      case 'flyby':
        this.flybyPose(target, dt, out);
        return;
      case 'orbit':
        this.orbitPose(target, dt, out);
        return;
      case 'free':
        this.dronePose(target, dt, out);
        return;
    }
  }

  private cockpitPose(target: CameraTarget, out: Pose): void {
    // The eye, in the world: the body-axes eye point rotated into the aircraft's attitude.
    scratchVector.copy(this.eyePointBody).applyQuaternion(target.attitude);
    // The head shift and the shake act in the *aircraft's* frame, not the world's — a pilot's
    // head compresses along their own spine, which is only "down" when the wings are level.
    scratchVectorB.set(0, -(this.gShiftM + this.shakeDisplacementM), 0).applyQuaternion(target.attitude);
    out.position.copy(target.position).add(scratchVector).add(scratchVectorB);
    out.orientation.copy(target.attitude);
    out.fovDeg = this.cockpitFovDeg;
    this.focus.copy(target.position);
  }

  private chasePose(target: CameraTarget, dt: number, out: Pose): void {
    const focus = this.focus.copy(target.position);
    focus.y += 1.6;

    const cosPitch = Math.cos(this.chasePitch);
    const ideal = scratchVector.set(
      focus.x + Math.sin(this.chaseYaw) * cosPitch * this.chaseDistance,
      focus.y + Math.sin(this.chasePitch) * this.chaseDistance,
      focus.z + Math.cos(this.chaseYaw) * cosPitch * this.chaseDistance,
    );

    /*
     * Smooth the OFFSET from the aeroplane, not the camera's position in the world.
     *
     * This used to `expDamp` the absolute world position toward `ideal`, and a first-order filter
     * chasing a target that is *moving* settles at a lag of `v·h/ln2` rather than at the target.
     * At the shipped 0.07 s half-life that is 0.101 s of travel — **27 m at 480 kt**, against a
     * nominal 22 m standoff. The consequences were both real and neither was a deliberate feel:
     *
     *   - **The standoff collapsed with airspeed.** `chaseYaw` is a fixed *world* bearing (see
     *     {@link orbit}) and never follows the aeroplane's heading, so the lag does not act along
     *     the boom — it subtracts from it. Measured against the shipping class, the 22 m standoff
     *     became **11.4 m at 480 kt** and about 7 m at the worst heading, putting the camera five
     *     metres above the canopy. The aeroplane changed apparent size as the pilot accelerated,
     *     with no input. `cameraRig.test.ts` pinned that at "about 10 m", which is how a defect
     *     acquires a test that guards it.
     *   - **Every frame-time excursion became world-slide.** The equilibrium lag is a function of
     *     `dt`, so a long frame moved the camera — up to 5.4° of scene swing on a dropped frame,
     *     about 106 px at 1080p, on the worst heading.
     *
     * Filtering the offset removes the ramp term identically: in steady flight the offset is
     * constant, a first-order filter tracks a constant with zero error, and the result is
     * dt-invariant to machine precision. The smoothing still does its job — it is the *change* of
     * offset, during an orbit or a dolly, that anyone wanted damped.
     *
     * It also makes the chase branch of {@link applyOriginShift} unnecessary: an offset is
     * origin-invariant, so a rebase cannot drag it. That call is kept and is now a no-op for this
     * mode rather than a correction, which is the right shape — the state simply no longer has a
     * frame to be wrong in.
     */
    const idealOffsetX = ideal.x - focus.x;
    const idealOffsetY = ideal.y - focus.y;
    const idealOffsetZ = ideal.z - focus.z;

    if (!this.chaseInitialised) {
      this.chaseOffset.set(idealOffsetX, idealOffsetY, idealOffsetZ);
      this.chaseInitialised = true;
    } else {
      const halfLife = 0.07;
      this.chaseOffset.set(
        expDamp(this.chaseOffset.x, idealOffsetX, halfLife, dt),
        expDamp(this.chaseOffset.y, idealOffsetY, halfLife, dt),
        expDamp(this.chaseOffset.z, idealOffsetZ, halfLife, dt),
      );
    }
    this.chasePosition.set(
      focus.x + this.chaseOffset.x,
      focus.y + this.chaseOffset.y,
      focus.z + this.chaseOffset.z,
    );

    out.position.copy(this.chasePosition);
    lookAt(out.orientation, out.position, focus);
    out.fovDeg = this.fovDeg;
  }

  /**
   * A tripod on the ground that the aircraft flies past, and which leapfrogs ahead of it.
   *
   * The leapfrog is what makes this a *view* rather than a fixed camera the aeroplane vanishes
   * from. When the aircraft has passed the tripod and is far enough away to be small, the tripod
   * jumps to a point ahead of it, offset to the side so the aeroplane crosses the frame rather
   * than flying into it. Offsetting to the *same* side each time would put the sun behind the
   * camera on every pass; alternating is what a camera operator would do.
   */
  private flybyLeapfrogCount = 0;
  private flybyPose(target: CameraTarget, _dt: number, out: Pose): void {
    const speed = target.velocity.length();
    const toTarget = scratchVector.subVectors(target.position, this.flybyPosition);
    const range = toTarget.length();

    // Ahead of it means "where it will be in a few seconds", so a fast aeroplane gets a longer
    // lead and a slow one is not left behind.
    const leadS = 4.5;
    const needsPlacing =
      !this.flybyPlaced ||
      range > 900 ||
      // Past the tripod and going away: the dot of the velocity with the line to the camera.
      (range > 140 && toTarget.dot(target.velocity) > 0);

    if (needsPlacing) {
      this.flybyLeapfrogCount++;
      const side = this.flybyLeapfrogCount % 2 === 0 ? 1 : -1;
      scratchVectorB.copy(target.velocity);
      if (scratchVectorB.lengthSq() < 1) scratchVectorB.set(0, 0, -1);
      scratchVectorB.normalize();
      // A lateral offset perpendicular to the flight path, in the horizontal plane.
      const lateral = scratchVectorB.z * side * 55;
      const lateralZ = -scratchVectorB.x * side * 55;

      this.flybyPosition
        .copy(target.position)
        .addScaledVector(scratchVectorB, Math.max(120, speed * leadS));
      this.flybyPosition.x += lateral;
      this.flybyPosition.z += lateralZ;
      // Stand the tripod on the ground under wherever the aircraft is, but never so far below
      // that the aeroplane is a dot in the sky.
      this.flybyPosition.y =
        target.position.y - Math.min(target.heightAboveGroundM, 60) + 3;
      this.flybyPlaced = true;
    }

    out.position.copy(this.flybyPosition);
    lookAt(out.orientation, out.position, target.position);
    // A little tighter than the free-flight field of view: a fly-by is a shot of the aeroplane.
    out.fovDeg = Math.max(25, this.fovDeg * 0.72);
    this.focus.copy(target.position);
  }

  private orbitPose(target: CameraTarget, dt: number, out: Pose): void {
    // Slow enough to read as a camera move rather than a spin: a full circuit in about 40 s.
    this.orbitAngle = wrapTwoPi(this.orbitAngle + dt * 0.157);
    const focus = this.focus.copy(target.position);
    const radius = this.chaseDistance * 1.4;
    out.position.set(
      focus.x + Math.sin(this.orbitAngle) * radius,
      focus.y + radius * 0.28,
      focus.z + Math.cos(this.orbitAngle) * radius,
    );
    lookAt(out.orientation, out.position, focus);
    out.fovDeg = this.fovDeg;
  }

  private dronePose(target: CameraTarget, dt: number, out: Pose): void {
    if (!this.dronePlaced) {
      // Start where the camera already is, so selecting the drone is a handover rather than a cut.
      this.dronePosition.copy(this.camera.position);
      scratchQuaternion.copy(this.camera.quaternion);
      // Recover yaw and pitch from the current orientation so the mouse picks up where the eye
      // was pointing instead of snapping to north.
      scratchVector.set(0, 0, -1).applyQuaternion(scratchQuaternion);
      this.droneYaw = Math.atan2(-scratchVector.x, -scratchVector.z);
      this.dronePitch = Math.asin(clamp(scratchVector.y, -1, 1));
      if (this.droneSpeedMps < 0.5) this.droneSpeedMps = 18;
      this.dronePlaced = true;
    }

    const cosPitch = Math.cos(this.dronePitch);
    const forward = scratchVector.set(
      -Math.sin(this.droneYaw) * cosPitch,
      Math.sin(this.dronePitch),
      -Math.cos(this.droneYaw) * cosPitch,
    );
    const right = scratchVectorB.set(Math.cos(this.droneYaw), 0, -Math.sin(this.droneYaw));

    const command = this.droneCommand;
    this.dronePosition
      .addScaledVector(forward, command.z * this.droneSpeedMps * dt)
      .addScaledVector(right, command.x * this.droneSpeedMps * dt);
    this.dronePosition.y += command.y * this.droneSpeedMps * dt;

    out.position.copy(this.dronePosition);
    scratchMatrixTarget.copy(this.dronePosition).add(forward);
    lookAt(out.orientation, out.position, scratchMatrixTarget);
    out.fovDeg = this.fovDeg;
    this.focus.copy(target.position);
  }

  /** Forget the accumulated state, e.g. on respawn. */
  reset(): void {
    this.chaseInitialised = false;
    this.flybyPlaced = false;
    this.dronePlaced = false;
    this.gShiftM = 0;
    this.shakeTimeS = Number.POSITIVE_INFINITY;
    this.shakeAmplitudeM = 0;
    this.wasOnGround = true;
    this.transition = 1;
    this.previousMode = null;
    this.fromSnapshot = false;
  }
}

/**
 * Point a quaternion from `eye` at `target`, with +Y up. Allocation-free.
 *
 * Written out rather than routed through `Object3D.lookAt` because the poses being built are not
 * attached to an `Object3D` — they are values, blended before anything is written to the camera.
 */
const lookForward = /* #__PURE__ */ new Vector3();
const lookRight = /* #__PURE__ */ new Vector3();
const lookUp = /* #__PURE__ */ new Vector3();
const lookMatrix = /* #__PURE__ */ new Float32Array(9);

function lookAt(out: Quaternion, eye: Vector3, target: Vector3): void {
  // Three's camera convention: it looks down its own −Z, so "forward" is the *negated* view
  // direction when building the basis.
  lookForward.subVectors(eye, target);
  if (lookForward.lengthSq() < 1e-10) lookForward.set(0, 0, 1);
  lookForward.normalize();

  lookRight.crossVectors(scratchUp, lookForward);
  if (lookRight.lengthSq() < 1e-10) {
    // Looking straight up or down: any right vector will do, so nudge off the pole.
    lookRight.set(1, 0, 0);
  }
  lookRight.normalize();
  lookUp.crossVectors(lookForward, lookRight);

  lookMatrix[0] = lookRight.x;
  lookMatrix[1] = lookRight.y;
  lookMatrix[2] = lookRight.z;
  lookMatrix[3] = lookUp.x;
  lookMatrix[4] = lookUp.y;
  lookMatrix[5] = lookUp.z;
  lookMatrix[6] = lookForward.x;
  lookMatrix[7] = lookForward.y;
  lookMatrix[8] = lookForward.z;
  quaternionFromBasis(out, lookMatrix);
}

/** Shepperd's method: pick the largest diagonal term, so no branch loses precision. */
function quaternionFromBasis(out: Quaternion, m: Float32Array): void {
  const m00 = m[0];
  const m01 = m[3];
  const m02 = m[6];
  const m10 = m[1];
  const m11 = m[4];
  const m12 = m[7];
  const m20 = m[2];
  const m21 = m[5];
  const m22 = m[8];

  const trace = m00 + m11 + m22;
  if (trace > 0) {
    const s = 0.5 / Math.sqrt(trace + 1);
    out.set((m21 - m12) * s, (m02 - m20) * s, (m10 - m01) * s, 0.25 / s);
  } else if (m00 > m11 && m00 > m22) {
    const s = 2 * Math.sqrt(1 + m00 - m11 - m22);
    out.set(0.25 * s, (m01 + m10) / s, (m02 + m20) / s, (m21 - m12) / s);
  } else if (m11 > m22) {
    const s = 2 * Math.sqrt(1 + m11 - m00 - m22);
    out.set((m01 + m10) / s, 0.25 * s, (m12 + m21) / s, (m02 - m20) / s);
  } else {
    const s = 2 * Math.sqrt(1 + m22 - m00 - m11);
    out.set((m02 + m20) / s, (m12 + m21) / s, 0.25 * s, (m10 - m01) / s);
  }
}
